import React, { useCallback, useEffect } from 'react';

import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { VerticalBarChart } from '../../../common/components/Charts/VerticalBarChart';
import {
  LabelValue,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import ValidationAlert from '../../../app/components/ValidationAlert';

import {
  DateRangeFilterApplied,
  SuperAdminDateRangeFilter,
} from './SuperAdminDateRangeFilter';
import {
  DateRangeState,
  initDefaultLastDaysRange,
  toDateEnd,
  toDateStart,
} from './SuperAdminTopCompaniesBase';

export const SuperAdminJudgmentTab: React.FC = () => {
  const spd = useSpdCore();
  const [dataJudgments, setDataJudgments] = React.useState<LabelValue[]>([]);
  const [dataJudgmentsByDays, setDataJudgmentsByDays] = React.useState<
    LabelValue[]
  >([]);
  const [filters, setFilters] = React.useState<DateRangeState>({
    dateFrom: '',
    dateTo: '',
  });
  const [applied, setApplied] = React.useState<DateRangeFilterApplied>({
    dateFrom: '',
    dateTo: '',
  });
  const [errors, setErrors] = React.useState<string[]>([]);
  const [errorsMonthly, setErrorsMonthly] = React.useState<string[]>([]);
  const [filtersMonthly, setFiltersMonthly] = React.useState<DateRangeState>({
    dateFrom: '',
    dateTo: '',
  });

  const loadByDays = useCallback(
    (a: DateRangeFilterApplied) => {
      setErrors([]);
      spd.reports
        .reportJudgments(
          true,
          toDateStart(a.dateFrom),
          toDateEnd(a.dateTo),
          a.companyId,
        )
        .then(data => setDataJudgmentsByDays(data))
        .catch(async error => {
          setDataJudgmentsByDays([]);
          setDataJudgments([]);
          console.error('Error during report judgments', error);
          setErrors(await responseErrors(error as ResponseError));
        });
    },
    [spd.reports],
  );

  const loadByMonths = useCallback(
    (a: DateRangeFilterApplied) => {
      setErrorsMonthly([]);
      spd.reports
        .reportJudgments(
          undefined,
          toDateStart(a.dateFrom),
          toDateEnd(a.dateTo),
          a.companyId,
        )
        .then(data => setDataJudgments(data))
        .catch(async error => {
          setDataJudgments([]);
          console.error('Error during report judgments (monthly)', error);
          setErrorsMonthly(await responseErrors(error as ResponseError));
        });
    },
    [spd.reports],
  );

  useEffect(() => {
    void initDefaultLastDaysRange(
      spd.params.getReportLastDays,
      range => {
        setFilters(range);
        setApplied(range);
        loadByDays(range);
      },
      async error => {
        console.error('Error during report last days param fetch', error);
        setErrors(await responseErrors(error as ResponseError));
      },
    );
  }, [spd.params, loadByDays]);

  useEffect(() => {
    loadByMonths({ dateFrom: '', dateTo: '' });
  }, [loadByMonths]);

  return (
    <>
      <SuperAdminDateRangeFilter
        filters={filters}
        setFilters={setFilters}
        withCompany={true}
        onApply={a => {
          setErrors([]);
          setDataJudgmentsByDays([]);
          setApplied(a);
          loadByDays(a);
        }}
      />
      <ValidationAlert
        show={errors.length > 0}
        errors={errors}
        className="mt-3"
      />
      {errors.length === 0 && (
        <VerticalBarChart
          title={`Zestawienie orzeczeń w zakresie: ${applied.dateFrom} - ${applied.dateTo}`}
          labels={dataJudgmentsByDays.map(o => o.label)}
          data={dataJudgmentsByDays.map(o => o.count)}
          datasetLabel="Liczba utworzonych orzeczeń"
          backgroundColor="rgba(34, 139, 34, 0.5)"
          borderColor="rgba(34, 139, 34, 1)"
          key="judgmentsByDays"
        />
      )}
      <SuperAdminDateRangeFilter
        filters={filtersMonthly}
        setFilters={setFiltersMonthly}
        withCompany={true}
        monthPicker={true}
        applyLabel="Pokaż zestawienie miesięczne"
        onApply={a => {
          setErrorsMonthly([]);
          setDataJudgments([]);
          loadByMonths(a);
        }}
      />
      <ValidationAlert
        show={errorsMonthly.length > 0}
        errors={errorsMonthly}
        className="mt-3"
      />
      {errorsMonthly.length === 0 && (
        <VerticalBarChart
          title="Zestawienie orzeczeń w podziale na miesiące"
          labels={dataJudgments.map(o => o.label)}
          data={dataJudgments.map(o => o.count)}
          datasetLabel="Liczba utworzonych orzeczeń"
          backgroundColor="rgba(112, 203, 92, 0.5)"
          borderColor="rgba(112, 203, 92, 1)"
          key="judgmentsByMonths"
        />
      )}
    </>
  );
};
